import { useLocalObservable } from 'mobx-react-lite';
import { EventInteractionArgs, OnDragStartArgs } from 'react-big-calendar/lib/addons/dragAndDrop';

import { armGlobalDragCleanup } from 'APP/utils/calendar/armGlobalDragCleanup';
import { checkAllDayRange } from 'APP/utils/calendar/checkAllDayRange';

import { CLASSNAME_ALL_DAY } from '../Content.constants';
import { IEventCalendar } from '../Content.types';

type IOnEditEventAfterDrop = (
  dragAndDropData: EventInteractionArgs<IEventCalendar>
) => Promise<void>;

export interface IDragAndDropPresenter {
  disarm: (() => void) | null;
  onEditEventAfterDrop: IOnEditEventAfterDrop | null;
  onDraggableAccessor(event: IEventCalendar): boolean;
  onDragStart(data: OnDragStartArgs<IEventCalendar>): void;
  onEventDrop(data: EventInteractionArgs<IEventCalendar>): Promise<void>;
  finishDrop(): void;
  setEditEventAfterDrop(onEditEventAfterDrop: IOnEditEventAfterDrop): void;
}

export const useDragAndDropPresenter = (): IDragAndDropPresenter => {
  const presenter = useLocalObservable<IDragAndDropPresenter>(() => ({
    events: [],
    disarm: null,
    onEditEventAfterDrop: null,

    setEditEventAfterDrop(onEditEventAfterDrop): void {
      presenter.onEditEventAfterDrop = onEditEventAfterDrop;
    },

    onDraggableAccessor(event: IEventCalendar): boolean {
      return event.account.providerEmail === event.initiatorEmail || !!event.isTemplate;
    },

    finishDrop(): void {
      document.documentElement.classList.remove(CLASSNAME_ALL_DAY);
    },

    onDragStart(data: OnDragStartArgs<IEventCalendar>): void {
      if (data?.event.allDay) {
        document.documentElement.classList.add(CLASSNAME_ALL_DAY);

        const clean = (): void => {
          if (!presenter.disarm) return;

          presenter.finishDrop();
          presenter.disarm?.();
          presenter.disarm = null;
        };
        presenter.disarm = armGlobalDragCleanup(clean);
      }
    },

    async onEventDrop(data: EventInteractionArgs<IEventCalendar>): Promise<void> {
      presenter.finishDrop();

      const targetAllDay = data.isAllDay ?? checkAllDayRange(data.start as Date, data.end as Date);

      if (targetAllDay !== data.event.allDay) {
        return;
      }

      await presenter.onEditEventAfterDrop?.(data);
    },
  }));

  return presenter;
};
